/**
 * The live route card at the top of a trip: an OpenStreetMap map (Leaflet) of
 * the driving route — pickup → programme stops in `order` → drop-off — the
 * real distance and driving time underneath, and one tap to turn-by-turn
 * navigation.
 *
 * Lifecycle follows MapRoutePanel.jsx (the admin screens' route map): the map
 * is built imperatively in an effect, deferred by a timer so React strict
 * mode's mount → unmount → mount builds one map (and sends one route request),
 * and removed on cleanup. The route line, distance and duration come from
 * OsrmRouteService.
 *
 * The map is a picture, not a map to explore. It sits in a scrolling page on a
 * phone, so no drag/zoom handler may claim a touch — with those off Leaflet
 * sets no `touch-action`, and the page scrolls straight through. The one thing
 * worth doing with it is navigating, so a tap on it opens navigation: the same
 * target as the Navigáció button, which is the keyboard and screen-reader path.
 * That target is where the driver is heading (`target`, portalFormat's
 * navigationTarget): the pickup, then the next stop or the drop-off.
 *
 * Degrades in two steps:
 *   - routing fails → the map stays, with a straight dashed line through the
 *     points and no distance/duration (never an invented number);
 *   - the map cannot start (missing coordinates, Leaflet throws, or no tile
 *     ever loads) → a plain address + Navigáció card, never an empty box.
 */
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import getRoute from '../../../Services/OsrmRouteService';
import { navigationUrl, toLatLon } from '../../../Utils/geoPoint';
import {
  navigationTarget, place, programmeHours, programmeStops, ROUTE_MAP_COPY,
} from '../portalFormat';
import { NavigateIcon } from './Icons';
import './PortalRouteMap.scss';

const OSM_TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_COPYRIGHT = 'https://www.openstreetmap.org/copyright';

const MAP_OPTIONS = {
  dragging: false,
  touchZoom: false,
  doubleClickZoom: false,
  scrollWheelZoom: false,
  boxZoom: false,
  keyboard: false, // otherwise Leaflet makes the container a tab stop
  zoomControl: false,
  // The OSM credit is rendered by React, outside role="img" (see below).
  attributionControl: false,
};

const FIT = { padding: [28, 28], maxZoom: 16 };
// No tile by then: treat the map as dead (captive airport wifi, blocked host).
const TILE_TIMEOUT_MS = 15000;

const AMBER = '#eba23a';
const INK = '#1a1a1a';

// Decorative only (the aria-label carries the meaning), so never focusable or
// clickable: a tap falls through to the map and opens navigation.
const MARKER = { interactive: false, keyboard: false };
const pinIcon = (kind, size) => L.divIcon({
  className: `pp-routemap__pin pp-routemap__pin--${kind}`,
  iconSize: [size, size],
  iconAnchor: [size / 2, size / 2],
});

// Two points this close are the same place (a stop geocoded a few metres off
// the pickup it was derived from).
const SAME_POINT_METRES = 50;
const EARTH_RADIUS_METRES = 6371000;

/** Great-circle distance between two {lat, lon} points, in metres. */
const metresBetween = (a, b) => {
  const rad = (deg) => (deg * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.min(1, Math.sqrt(h)));
};

const samePoint = (a, b) => metresBetween(a, b) <= SAME_POINT_METRES;

/**
 * Pickup, drop-off and the programme's located stops in `order`; null if
 * unmappable. On real bookings `destination_from` / `destination_to` are
 * derived from the first and last programme stop, so a stop that sits on the
 * pickup or the drop-off is that end again — it is dropped, or the map would
 * stack two markers, send the route service a duplicate waypoint and count a
 * stop that is not one.
 */
const routePlan = (trip) => {
  const from = toLatLon(trip.from);
  const to = toLatLon(trip.to);
  if (!from || !to) return null;
  const stops = programmeStops(trip)
    .map(toLatLon)
    .filter(Boolean)
    .filter((stop) => !samePoint(stop, from) && !samePoint(stop, to));
  return { from, to, stops };
};

/**
 * `copy` names the card and formats distance/duration: the driver's
 * Hungarian by default (ROUTE_MAP_COPY), the operator screens' English when
 * passed — with a `target` in the same language.
 */
export default function PortalRouteMap({
  trip, target = navigationTarget(trip), copy = ROUTE_MAP_COPY,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [route, setRoute] = useState(null);
  const [failed, setFailed] = useState(false);

  const plan = routePlan(trip);
  // Serialised so a fresh trip object with the same route (every progress tap
  // re-renders from the API) neither refetches nor rebuilds the map.
  const planKey = plan ? JSON.stringify(plan) : '';

  const href = navigationUrl(target.point);
  const hrefRef = useRef(href);
  hrefRef.current = href;

  useEffect(() => {
    setRoute(null);
    setFailed(false);
    if (!planKey) return undefined;

    const { from, to, stops } = JSON.parse(planKey);
    const points = [from, ...stops, to].map((p) => [p.lat, p.lon]);
    let cancelled = false;
    let tileTimer;

    const removeMap = () => {
      clearTimeout(tileTimer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };

    const giveUp = () => {
      if (cancelled) return;
      removeMap();
      setFailed(true);
    };

    const drawRoute = (map, result) => {
      // GeoJSON is [lon, lat]; Leaflet wants [lat, lon].
      const line = result.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
      L.polyline(line, {
        color: INK, weight: 8, opacity: 0.85, interactive: false,
      }).addTo(map);
      L.polyline(line, { color: AMBER, weight: 4.5, interactive: false }).addTo(map);
      map.fitBounds(line, FIT);
    };

    // Routing failed: show where, not how — and no numbers.
    const drawStraight = (map) => {
      L.polyline(points, {
        color: INK, weight: 3, opacity: 0.7, dashArray: '6 8', interactive: false,
      }).addTo(map);
    };

    const start = () => {
      let map;
      try {
        map = L.map(containerRef.current, MAP_OPTIONS);
        mapRef.current = map;
        map.fitBounds(points, FIT);

        let tileLoaded = false;
        const tiles = L.tileLayer(OSM_TILES, { maxZoom: 19 });
        tiles.on('tileload', () => {
          tileLoaded = true;
          clearTimeout(tileTimer);
        });
        // 'load' fires once every visible tile has settled, errors included:
        // if none of them made it, the map is an empty box.
        tiles.on('load', () => {
          if (!tileLoaded) giveUp();
        });
        tiles.addTo(map);
        tileTimer = setTimeout(() => {
          if (!tileLoaded) giveUp();
        }, TILE_TIMEOUT_MS);

        // Added in paint order: stops, then drop-off, pickup on top.
        stops.forEach((stop) => {
          L.marker([stop.lat, stop.lon], { ...MARKER, icon: pinIcon('stop', 14) }).addTo(map);
        });
        L.marker([to.lat, to.lon], { ...MARKER, icon: pinIcon('dropoff', 20) }).addTo(map);
        L.marker([from.lat, from.lon], { ...MARKER, icon: pinIcon('pickup', 22) }).addTo(map);

        map.on('click', () => {
          window.open(hrefRef.current, '_blank', 'noopener,noreferrer');
        });
      } catch (error) {
        giveUp();
        return;
      }

      getRoute(from, to, stops)
        .then((result) => {
          if (cancelled || mapRef.current !== map) return;
          if (!result) {
            drawStraight(map);
            return;
          }
          drawRoute(map, result);
          setRoute({ distance: result.distance, duration: result.duration });
        })
        .catch(() => {
          if (!cancelled && mapRef.current === map) drawStraight(map);
        });
    };

    const initTimer = setTimeout(start, 50);

    return () => {
      cancelled = true;
      clearTimeout(initTimer);
      removeMap();
    };
  }, [planKey]);

  if (!href) return null;

  const showMap = Boolean(plan) && !failed;
  const distance = showMap ? copy.distance(route?.distance) : '';
  // A ten-hour tour is not a 34-minute drive. Where the trip is booked as a
  // programme the card gives the programme's OWN length instead of the route
  // service's estimate for driving A→B — and gives it even with no map and no
  // route, because it is a fact of the booking rather than anything the map
  // worked out. The distance is the route's either way, so it stays put.
  const drive = showMap ? copy.duration(route?.duration) : '';
  const hours = programmeHours(trip);
  const duration = hours ? copy.programmeDuration(hours) : drive;
  const summary = [distance, duration].filter(Boolean).join(' · ');
  const pickup = place(trip.from);
  const heading = place(target.point);
  const stopCount = plan?.stops.length || 0;
  const label = [
    `${copy.route}: ${pickup.title || copy.pickup} → ${place(trip.to).title || copy.dropoff}`,
    stopCount ? copy.stops(stopCount) : '',
    distance,
    duration,
  ].filter(Boolean).join(', ');

  return (
    <div className="pp-card pp-card--flush pp-routemap">
      {showMap ? (
        <div className="pp-routemap__frame">
          <div ref={containerRef} className="pp-routemap__map" role="img" aria-label={label} />
          <a className="pp-routemap__credit" href={OSM_COPYRIGHT} target="_blank" rel="noreferrer">
            © OpenStreetMap contributors
          </a>
        </div>
      ) : null}
      <div className="pp-routemap__body">
        <div className="pp-routemap__text">
          {summary ? <div className="pp-routemap__summary">{summary}</div> : null}
          {heading.address ? (
            <div className="pp-routemap__address">{`${target.label}: ${heading.address}`}</div>
          ) : null}
        </div>
        <a
          className="pp-btn pp-btn--outline pp-routemap__nav"
          href={href}
          target="_blank"
          rel="noreferrer"
        >
          <NavigateIcon size={18} />
          {copy.navigate}
        </a>
      </div>
    </div>
  );
}
